import React, { useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  CalendarToday,
  Folder,
  Delete,
  Close,
  Star,
  StarBorder,
  Settings,
  CalendarMonth
} from '@mui/icons-material';
import { deleteSchedule, deleteScheduleUser, getSchedule, putSchedule, putScheduleUser } from '../../lib/connector/scheduleQueries';
import { getUserPool } from '../../lib/pools/pools';
import { v4 as uuidv4 } from 'uuid';
import { validateAuth } from '../../lib/Auth/Auth';
import { deletePosition, getPositions, putPosition } from '../../lib/connector/positionQueries';
import { useToast } from '../modals/ToastProvider/ToastProvider';
import { deleteShift, getShifts } from '../../lib/connector/shiftQueries';
import EditScheduleModal from '../modals/EditScheduleModal';
import { Schedule } from '../../pages/Dashboard/Dashboard';
import moment from 'moment';
import { lightTheme } from '../../contexts/ThemeManager/ThemeManager';
import { checkAccessLevel, updateUserAttribute } from '../../lib/authenticate';
import { getAuth, setAuth } from '../../contexts/authStore';

const DRAWER_WIDTH = 320;

export const COLOR_OPTIONS = [
  { name: 'Stafflows Blue', value: '#0B74D1', light: '#E3F2FD' },
  { name: 'Purple', value: '#9C27B0', light: '#F3E5F5' },
  { name: 'Green', value: '#4CAF50', light: '#E8F5E9' },
  { name: 'Stafflows Orange', value: '#f57c00', light: '#FFF3E0' },
  { name: 'Red', value: '#F44336', light: '#FFEBEE' },
  { name: 'Teal', value: '#009688', light: '#E0F2F1' },
  { name: 'Pink', value: '#E91E63', light: '#FCE4EC' },
  { name: 'Indigo', value: '#3F51B5', light: '#E8EAF6' },
  { name: 'Cyan', value: '#00BCD4', light: '#E0F7FA' },
  { name: 'Amber', value: '#FFC107', light: '#FFF8E1' },
  { name: 'Deep Purple', value: '#673AB7', light: '#EDE7F6' },
  { name: 'Lime', value: '#CDDC39', light: '#F9FBE7' },
];

interface DashboardSidebarProps {
  open: boolean;
  onToggle: () => void;
  setSelectedSchedule: (schedule: Schedule) => void;
  selectedSchedule: Schedule | null;
  userData: Record<string, any>;
  accessLevel: string | null;
  shifts: any[];
}

export default function DashboardSidebar({ open, onToggle, setSelectedSchedule, selectedSchedule, userData, accessLevel, shifts }: DashboardSidebarProps) {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [schedules, setSchedules] = useState<any[]>([]);
  const [newScheduleFrequency, setNewScheduleFrequency] = useState('weekly');

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeMenu, setActiveMenu] = useState<Schedule | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newScheduleName, setNewScheduleName] = useState('');
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);

  React.useEffect(() => {
    if (validateAuth()) {
      const fetchSchedules = async () => {
        let schedules: any[] = [];
        const scheduleIds = getAuth().user_type?.split(",") || [];
        
        for (const ownerScheduleId of scheduleIds) {
          const response = await getSchedule(ownerScheduleId.split("#")[0], ownerScheduleId.split("#")[1]);
          schedules.push(response[0]);
        }
        
        setSchedules(schedules);
        setSelectedSchedule(userData.primarySchedule ? schedules.find((schedule: { scheduleId: any; }) => schedule.scheduleId === userData.primarySchedule) : schedules[0]);
      };
      
      fetchSchedules();
    }
  }, [])

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, schedule: Schedule) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setActiveMenu(schedule);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveMenu(null);
  };

  const getScheduleDate = (frequency: string, type: 'last' | 'active' = 'active'): string => {
    const now = moment();

    switch (frequency) {
      case 'weekly':
        // Get last Sunday (start of current week)
        if (type === 'last') {
          return now.clone().startOf('week').format("YYYY-MM-DD");
        } else {
          return now.clone().endOf('week').format("YYYY-MM-DD");
        }

      case 'bi-weekly':
        // Get last Sunday of the previous week (2 weeks ago)
        if (type === 'last') {
          return now.clone().subtract(1, 'week').startOf('week').format("YYYY-MM-DD");
        } else {
          return now.clone().endOf('week').format("YYYY-MM-DD");
        }

      case 'monthly':
        // Get first day of current month
        if (type === 'last') {
          return now.clone().startOf('month').format("YYYY-MM-DD");
        } else {
          return now.clone().endOf('month').format("YYYY-MM-DD");
        }

      default:
        // Default to current date
        return now.format("YYYY-MM-DD");
    }
  };

  const handleCreateSchedule = () => {
    if (newScheduleName.trim()) {
      try {
        const newSchedule = {
          scheduleId: uuidv4(),
          name: newScheduleName,
          favorite: false,
          color: COLOR_OPTIONS[Math.floor(Math.random() * COLOR_OPTIONS.length)].value,
          shiftsCount: 0,
          employees: [],
          link: '',
          userId: getUserPool()!.getCurrentUser()!.getUsername(),
          lastActiveScheduleDate: getScheduleDate(newScheduleFrequency, 'last'),
          activeScheduleFrequency: newScheduleFrequency,
          allowSwapWithoutApproval: false,
          allowPickUpWithoutApproval: false,
          viewOtherStaff: false,
          availabilityScheduleRequiredTime: 1,
          activeScheduleDate: getScheduleDate(newScheduleFrequency, 'active'),
          lastSubmitted: moment().format("YYYY-MM-DD"),
          primarySchedule: false
        };

        putSchedule(
          getUserPool()!.getCurrentUser()!.getUsername(),
          newSchedule.scheduleId,
          newSchedule.name,
          newSchedule.color,
          '',
          [],
          false,
          false,
          false,
          false,
          newSchedule.activeScheduleFrequency,
          newSchedule.lastActiveScheduleDate,
          newSchedule.availabilityScheduleRequiredTime,
          newSchedule.activeScheduleDate,
          newSchedule.lastSubmitted,
        );
        const updatedUserType = getAuth().user_type && getAuth().user_type !== "" ? getAuth().user_type + "," : "";
        updateUserAttribute("custom:user_type",
          updatedUserType + getUserPool()!.getCurrentUser()!.getUsername() + "#" + newSchedule.scheduleId);
        setAuth({ user_type: updatedUserType + getUserPool()!.getCurrentUser()!.getUsername() + "#" + newSchedule.scheduleId } as any);
        putPosition(newSchedule.scheduleId, uuidv4(), "Admin", [getUserPool()!.getCurrentUser()!.getUsername()!]);
        putScheduleUser(getUserPool()!.getCurrentUser()!.getUsername(), newSchedule.scheduleId + "#" + getUserPool()!.getCurrentUser()!.getUsername(), userData.name, "admin");

        setSchedules([...schedules, newSchedule]);
        setSelectedSchedule(newSchedule);
        setNewScheduleName('');
        setNewScheduleFrequency('weekly'); // Reset to default
        setShowCreateDialog(false);
        toast.showSuccess(newScheduleName + ' - created successfully.');
      }
      catch (error: any) {
        toast.showError('Error creating schedule. Please try again.', error.message);
      }
    }
  };

  const handleUpdateSchedule = async (
    scheduleId: string,
    name: string,
    color: string,
    favorite: boolean,
    activeScheduleFrequency: string,
    availabilityScheduleRequiredTime: number,
    settings: {
      allowSwapWithoutApproval: boolean,
      allowPickUpWithoutApproval: boolean,
      showOtherStaff: boolean,
    }
  ) => {
    await putSchedule(activeMenu!.userId, activeMenu!.scheduleId, name, color,
      activeMenu!.link, activeMenu!.employees, favorite, settings.allowSwapWithoutApproval,
      settings.allowPickUpWithoutApproval, settings.showOtherStaff, activeScheduleFrequency,
      activeMenu!.lastActiveScheduleDate, availabilityScheduleRequiredTime, activeMenu!.activeScheduleDate,
      activeMenu!.lastSubmitted);

    // Refresh schedules list
    setSchedules(schedules.map(s =>
      s.scheduleId === scheduleId ? { ...s, name, color, favorite } : s
    ));



  };

  const handleToggleFavorite = (scheduleId: string) => {
    const schedule = schedules.find(s => s.scheduleId === scheduleId);
    if (!schedule) return;
    putSchedule(activeMenu!.userId, schedule.scheduleId, schedule.name, schedule.color,
      schedule.link, schedule.employees, !schedule.favorite, schedule.allowSwapWithoutApproval,
      schedule.allowPickUpWithoutApproval, schedule.viewOtherStaff, schedule.activeScheduleFrequency,
      schedule.lastActiveScheduleDate, schedule.availabilityScheduleRequiredTime, schedule.activeScheduleDate,
      schedule.lastSubmitted);
    setSchedules(schedules.map(s =>
      s.scheduleId === scheduleId ? { ...s, favorite: !s.favorite } : s
    ));
    handleMenuClose();
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    try {
      let canDelete = true
      schedules.map((s) => {
        if (s.scheduleId === scheduleId) {
          if (s.employees && s.employees.length > 0) {
            toast.showError('Cannot delete schedule with employees. Please remove all employees before deleting the schedule.');
            canDelete = false;
            return;
          }
        }
      })
      if (!canDelete) { return; }
      deleteSchedule(activeMenu!.userId, scheduleId).then(() => {
        getPositions(scheduleId).then((positions) => {
          positions.map((position: any) => {
            deletePosition(scheduleId, position.position);
          })
        })
        getShifts(scheduleId).then((shifts) => {
          shifts.map((shift: any) => {
            deleteShift(scheduleId, shift.shiftId);
          })
        });
        const schedule = schedules.find(s => s.scheduleId === scheduleId);
        if (schedule && schedule.employees) {
          schedule.employees.map((employee: string) => {
            deleteScheduleUser(employee, scheduleId + "#" + schedule.userId);
          });
        }
      })
      setSchedules(schedules.filter(s => s.scheduleId !== scheduleId));
      if (selectedSchedule?.scheduleId === scheduleId) {
        setSelectedSchedule(schedules[0]);
      }
      handleMenuClose();
      toast.showSuccess('Schedule deleted successfully.');
    } catch (error: any) {
      toast.showError('Error deleting schedule. Please try again.', error.message);
    }
  };

  const filteredSchedules = schedules.filter(schedule =>
    schedule.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const favoriteSchedules = filteredSchedules.filter(s => s.favorite);
  const otherSchedules = filteredSchedules.filter(s => !s.favorite);

  return (
    <>
      {/* Sidebar Toggle Button */}
      <IconButton
        onClick={onToggle}
        sx={{
          position: 'fixed',
          left: open ? DRAWER_WIDTH + 16 : 16,
          top: { xs: 60, sm: 80, md: 80, lg: 80 },
          zIndex: 1300,
          bgcolor: 'info.main',
          color: lightTheme.palette.background.paper,
          boxShadow: 2,
          transition: 'left 0.3s',
          '&:hover': { bgcolor: 'info.light' }
        }}
      >
        {open ? <Close /> : <CalendarMonth />}
      </IconButton>

      {/* Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper'
          },
        }}
      >
        {/* Sidebar Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday color="secondary" />
              <Typography variant="h6" fontWeight="bold">
                Schedules
              </Typography>
            </Box>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search schedules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Create New Schedule Button */}
        {userData.accountType === "owner" ? <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Add />}
            onClick={() => setShowCreateDialog(true)}
          >
            New Schedule
          </Button>
        </Box> : <></>}

        {/* Schedule Lists */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {/* Favorites */}
          {favoriteSchedules.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 2, py: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Favorites
              </Typography>
              <List disablePadding>
                {favoriteSchedules.map((schedule) => (
                  <ListItem
                    key={schedule.scheduleId}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleMenuOpen(e, schedule)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      selected={selectedSchedule?.scheduleId === schedule.scheduleId}
                      onClick={() => setSelectedSchedule(schedule)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.50',
                          '&:hover': {
                            bgcolor: 'primary.100',
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            bgcolor: schedule.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Folder sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={schedule.name}
                        secondary={`${schedule.employees?.length} staff`}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                      <Star sx={{ fontSize: 16, color: 'warning.main', mr: 1 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* All Schedules */}
          {otherSchedules.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 2, py: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase' }}
              >
                All Schedules
              </Typography>
              <List disablePadding>
                {otherSchedules.map((schedule) => (
                  <ListItem
                    key={schedule.scheduleId}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleMenuOpen(e, schedule)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      selected={selectedSchedule?.scheduleId === schedule.scheduleId}
                      onClick={() => {
                        setSelectedSchedule(schedule)
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.50',
                          '&:hover': {
                            bgcolor: 'primary.100',
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            bgcolor: schedule.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Folder sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={schedule.name}
                        secondary={`${schedule.employees?.length} staff`}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Empty State */}
          {filteredSchedules.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
              <CalendarToday sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {searchQuery ? 'No schedules found' : 'No schedules yet'}
              </Typography>
              {!searchQuery && (
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setShowCreateDialog(true)}
                  sx={{ mt: 2 }}
                >
                  Create your first schedule
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Current Selection Info */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Currently Viewing
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: selectedSchedule?.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Folder sx={{ fontSize: 18, color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {selectedSchedule?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedSchedule?.employees?.length} staff | {shifts.filter(shift => shift.scheduleId === selectedSchedule?.scheduleId && shift.isRecurring).length} regular shifts
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        disableEnforceFocus
      >
        <MenuItem onClick={() => activeMenu && handleToggleFavorite(activeMenu.scheduleId)}>
          <ListItemIcon>
            {activeMenu?.favorite ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {activeMenu?.favorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </ListItemText>
        </MenuItem>
        {checkAccessLevel(accessLevel) ? <MenuItem onClick={() => setShowEditScheduleModal(true)}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem> : <></>}
        {userData.accountType === "owner" ? <><Divider /><MenuItem
          onClick={() => { if (activeMenu) handleDeleteSchedule(activeMenu.scheduleId); }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Schedule</ListItemText>
        </MenuItem></> : <></>}
      </Menu>

      {/* Create Schedule Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create New Schedule</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Schedule Name"
            value={newScheduleName}
            onChange={(e) => setNewScheduleName(e.target.value)}
            placeholder="e.g., Summer 2025, Weekend Shifts"
            sx={{ mt: 1, mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 1 }}>
            <InputLabel>Schedule Frequency</InputLabel>
            <Select
              value={newScheduleFrequency}
              label="Schedule Frequency"
              onChange={(e) => setNewScheduleFrequency(e.target.value)}
            >
              <SelectMenuItem value="weekly">Weekly</SelectMenuItem>
              <SelectMenuItem value="bi-weekly">Bi-weekly</SelectMenuItem>
              <SelectMenuItem value="monthly">Monthly</SelectMenuItem>
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {newScheduleFrequency === 'weekly' && 'Schedule will start from this Sunday'}
            {newScheduleFrequency === 'bi-weekly' && 'Schedule will start from last Sunday of previous week'}
            {newScheduleFrequency === 'monthly' && 'Schedule will start from the first day of this month'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowCreateDialog(false);
            setNewScheduleName('');
            setNewScheduleFrequency('weekly');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateSchedule}
            variant="contained"
            disabled={!newScheduleName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>


      <EditScheduleModal
        open={showEditScheduleModal}
        onClose={() => setShowEditScheduleModal(false)}
        schedule={activeMenu}
        onSave={handleUpdateSchedule}
        existingSchedules={schedules}
      />
    </>
  );
}